import { Card } from "primereact/card";
import React from "react";
import * as client from "../../../client";
import { useAllSovPivots } from "../data";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import styles from "./SovOverviewCard.module.css";
import { formatExactMoneyDollars } from "../Money";
import { sovPivotColumnMapping } from "../dashboard/util";

type SovOverviewCardProps = {
  specialtyPropertyInfo: client.ReportResponse;
};

type PivotGroupProps = {
  pivots: Record<string, client.PivotTableResult>;
  labelMap: Record<string, string>;
};

const PIVOT_ORDER = [
  "state",
  "year_built",
  "construction_description",
  "occupancy_description",
  "flood_zone",
  "sprinkler",
  "rms_dist_to_coast",
];

const PivotGroup: React.FC<PivotGroupProps> = ({ pivots, labelMap }) => {
  const pivotEntries = PIVOT_ORDER.filter((key) => key in pivots).map(
    (key) => [key, pivots[key]] as [string, client.PivotTableResult]
  );

  return (
    <div className={styles.pivotContainer}>
      {pivotEntries.map(([pivotKey, pivot_data]) => {
        const total = pivot_data.total_row;
        const label = labelMap[pivotKey];

        return (
          <div key={pivotKey} className={styles.pivotCard}>
            <h4 className={styles.pivotCardTitle}>{label}</h4>
            <div className={styles.pivotTableWrap}>
              <DataTable
                stripedRows
                value={pivot_data.rows}
                sortMode="multiple"
              >
                <Column
                  field="group_value"
                  header={label}
                  sortable
                  footer={total?.group_value}
                />
                <Column
                  field="location_count"
                  header="Count"
                  sortable
                  footer={String(total?.location_count ?? "")}
                />
                <Column
                  field="total_tiv"
                  header="TIV"
                  sortable
                  body={(r) => (
                    <div>
                      {formatExactMoneyDollars(r.total_tiv, {
                        withRounding: true,
                        withDollarSign: true,
                      })}
                    </div>
                  )}
                  footer={
                    total
                      ? formatExactMoneyDollars(Number(total.total_tiv), {
                          withRounding: true,
                          withDollarSign: true,
                        })
                      : ""
                  }
                />
                <Column
                  field="percent_of_tiv"
                  header="% of TIV"
                  body={(r) => (r ? `${r.percent_of_tiv}%` : "")}
                  sortable
                  footer={total ? `${total.percent_of_tiv}%` : ""}
                />
              </DataTable>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const SovOverviewCard: React.FC<SovOverviewCardProps> = ({
  specialtyPropertyInfo,
}) => {
  const allPivots = useAllSovPivots(specialtyPropertyInfo.report_id);

  const pivotsMap = allPivots.data?.all_pivots?.pivots ?? {};
  const pivotEntries = Object.entries(pivotsMap);

  const labelMap = sovPivotColumnMapping();

  return (
    <Card title="Pivot Tables">
      {allPivots.isPending ? (
        <div className={styles.pivotSpinner}>
          <ProgressSpinner />
        </div>
      ) : pivotEntries.length > 0 ? (
        <PivotGroup pivots={pivotsMap} labelMap={labelMap} />
      ) : (
        <div>No pivot data</div>
      )}
    </Card>
  );
};
